import { inject } from '@angular/core';

import { IFederatedLoginEnvironmentVariables } from '@resident-nx/shared';
import { KeycloakService, KeycloakTokenStorageKey } from './keycloak.service';
import { LocalStorageService } from '../storage/local-storage.service';

export function initializeKeycloak() {
  return () => {
    const keycloakService = inject(KeycloakService);
    const localStorageService = inject(LocalStorageService);

    const clientId = localStorageService.getItem(
      IFederatedLoginEnvironmentVariables.CLIENT_ID
    ) as string;
    const realm = localStorageService.getItem(IFederatedLoginEnvironmentVariables.REALM) as string;
    const url = localStorageService.getItem(
      IFederatedLoginEnvironmentVariables.BASE_URL_AUTH
    ) as string;

    if (!clientId) return Promise.resolve(true);

    // Restore an impersonation session (see ImpersonateComponent) across
    // reloads. There is no refreshable Keycloak session behind an impersonation
    // token, so we re-inject it via initImpersonation instead of the normal
    // init()/check-sso flow and return early.
    const impersonationToken =
      (localStorageService.getItem(KeycloakTokenStorageKey.IMPERSONATION_TOKEN) as string) ||
      undefined;
    const impersonationRefreshToken =
      (localStorageService.getItem(
        KeycloakTokenStorageKey.IMPERSONATION_REFRESH_TOKEN
      ) as string) || undefined;
    if (impersonationToken && realm && url) {
      keycloakService.initImpersonation(
        { url, realm, clientId },
        impersonationToken,
        impersonationRefreshToken
      );
      return Promise.resolve(true);
    }

    // Restore persisted tokens so Keycloak can re-establish the session via the
    // refresh token (a direct token-endpoint POST, no cookie/iframe) instead of
    // the silent check-sso iframe, which is blocked by third-party cookie
    // restrictions on custom domains. keycloak-js validates the access token and
    // silently refreshes it when expired.
    const token =
      (localStorageService.getItem(KeycloakTokenStorageKey.TOKEN) as string) || undefined;
    const refreshToken =
      (localStorageService.getItem(KeycloakTokenStorageKey.REFRESH_TOKEN) as string) || undefined;
    const idToken =
      (localStorageService.getItem(KeycloakTokenStorageKey.ID_TOKEN) as string) || undefined;

    return keycloakService.init({ url, realm, clientId }, { token, refreshToken, idToken });
  };
}
